import { useEffect } from 'react'
import { X, ArrowRight, MessageSquare, RefreshCw, CheckCircle, XCircle } from 'lucide-react'

import type { SkerpaGuidanceItem } from '@ispot/shared'

import { useDismissGuidance, useCompleteGuidance } from '@/hooks/useGuidance'
import { useStartValidationAnalysis } from '@/hooks/useValidationAnalysis'

const PRIORITY_COLOR: Record<string, string> = {
  high:   'var(--color-danger)',
  medium: 'var(--color-warning)',
  low:    'var(--color-fg-muted)',
}

const DOMAIN_COLOR: Record<string, string> = {
  market:   'var(--color-market)',
  business: 'var(--color-business)',
  solution: 'var(--color-solution)',
}

interface GuidanceDetailPanelProps {
  item: SkerpaGuidanceItem
  onClose: () => void
  onViewChange?: (view: string) => void
}

export function GuidanceDetailPanel({ item, onClose, onViewChange }: GuidanceDetailPanelProps) {
  const dismiss  = useDismissGuidance()
  const complete = useCompleteGuidance()
  const startAnalysis = useStartValidationAnalysis()

  useEffect(() => {
    const handler = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose() }
    document.addEventListener('keydown', handler)
    return () => document.removeEventListener('keydown', handler)
  }, [onClose])

  const handleAction = (kind: string, target?: string, prompt?: string) => {
    if (kind === 'navigate' && target) { onViewChange?.(target); onClose() }
    else if (kind === 'open_chat' && prompt) { /* future: open Skerpa chat with prompt */ onClose() }
    else if (kind === 'run_analysis') { startAnalysis.mutate(); onClose() }
  }

  const handleDismiss = () => { dismiss.mutate(item.id); onClose() }
  const handleComplete = () => { complete.mutate(item.id); onClose() }

  const createdDate = new Date(item.createdAt).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })
  const priorityDot = PRIORITY_COLOR[item.priority] ?? 'var(--color-fg-muted)'
  const domainColor = DOMAIN_COLOR[item.domain] ?? 'var(--color-fg-muted)'

  return (
    <div
      className="detail-drawer-enter flex-shrink-0 flex flex-col"
      style={{
        width: 380,
        borderLeft: '1px solid var(--color-border)',
        background: 'var(--color-bg-secondary)',
        boxShadow: '-8px 0 32px rgba(29,26,21,0.05), -2px 0 8px rgba(29,26,21,0.04)',
        animation: 'detailDrawerSlideIn 180ms cubic-bezier(0.2, 0.7, 0.2, 1)',
        overflow: 'hidden',
      }}
    >
      {/* Header */}
      <div className="flex items-center justify-between flex-shrink-0" style={{ padding: '12px 16px 10px', borderBottom: '1px solid var(--color-border)' }}>
        <div className="flex items-center gap-2">
          <span style={{ width: 7, height: 7, borderRadius: '50%', backgroundColor: priorityDot, flexShrink: 0 }} />
          <span className="font-mono uppercase" style={{ fontSize: 10, letterSpacing: '0.1em', color: priorityDot }}>{item.priority}</span>
          {item.domain !== 'cross' && (
            <>
              <span style={{ color: 'var(--color-border-strong)' }}>·</span>
              <span className="font-mono uppercase" style={{ fontSize: 10, letterSpacing: '0.1em', color: domainColor }}>{item.domain}</span>
            </>
          )}
        </div>
        <button
          type="button"
          onClick={onClose}
          className="p-1 rounded hover:bg-bg-card transition-colors text-text-muted hover:text-text"
        >
          <X className="w-3.5 h-3.5" />
        </button>
      </div>

      {/* Body — scrollable */}
      <div style={{ flex: 1, overflowY: 'auto', padding: '16px' }}>
        <h3 style={{ fontFamily: 'var(--font-display)', fontSize: 16, fontWeight: 600, lineHeight: 1.3, letterSpacing: '-0.01em', color: 'var(--color-fg)', marginBottom: 10 }}>
          {item.title}
        </h3>

        <p style={{ fontSize: 13, lineHeight: 1.65, color: 'var(--color-fg-secondary)', marginBottom: 16 }}>
          {item.description}
        </p>

        {/* Actions */}
        {item.actions.length > 0 && (
          <section style={{ marginBottom: 16 }}>
            <div className="font-mono uppercase text-text-muted" style={{ fontSize: 9.5, letterSpacing: '0.1em', marginBottom: 8 }}>Actions</div>
            <div className="flex flex-col gap-1.5">
              {item.actions.map((action, i) => (
                <button
                  key={i}
                  type="button"
                  onClick={() => handleAction(action.kind, action.target, action.prompt)}
                  className="flex items-center gap-2 w-full text-left rounded-md hover:bg-bg-card transition-colors group"
                  style={{ padding: '7px 10px', border: '1px solid var(--color-border)', background: 'var(--color-bg-card)' }}
                >
                  {action.kind === 'navigate'     && <ArrowRight className="w-3.5 h-3.5 flex-shrink-0 text-text-muted group-hover:text-text transition-colors" />}
                  {action.kind === 'open_chat'    && <MessageSquare className="w-3.5 h-3.5 flex-shrink-0 text-text-muted group-hover:text-text transition-colors" />}
                  {action.kind === 'run_analysis' && <RefreshCw className="w-3.5 h-3.5 flex-shrink-0 text-text-muted group-hover:text-text transition-colors" />}
                  <span style={{ fontSize: 12.5, color: 'var(--color-fg-secondary)', fontWeight: 500 }}>{action.label}</span>
                </button>
              ))}
            </div>
          </section>
        )}

        {/* Evidence refs */}
        {item.evidenceRefs && item.evidenceRefs.length > 0 && (
          <section style={{ marginBottom: 16 }}>
            <div className="font-mono uppercase text-text-muted" style={{ fontSize: 9.5, letterSpacing: '0.1em', marginBottom: 8 }}>Evidence</div>
            <div className="flex flex-col gap-1">
              {item.evidenceRefs.map((ref, i) => (
                <div key={i} className="flex items-center gap-2">
                  <span style={{ width: 4, height: 4, borderRadius: '50%', backgroundColor: 'var(--color-fg-muted)', flexShrink: 0 }} />
                  <span style={{ fontSize: 12, color: 'var(--color-fg-muted)' }}>{ref}</span>
                </div>
              ))}
            </div>
          </section>
        )}

        {/* Source */}
        <div className="font-mono text-text-muted" style={{ fontSize: 10, marginTop: 'auto', paddingTop: 4 }}>
          Created {createdDate} · via {item.source}
        </div>
      </div>

      {/* Footer actions */}
      <div className="flex items-center gap-2 flex-shrink-0" style={{ padding: '10px 16px', borderTop: '1px solid var(--color-border)' }}>
        <button
          type="button"
          onClick={handleComplete}
          disabled={complete.isPending}
          className="flex items-center gap-1.5 rounded-md transition-colors disabled:opacity-50 hover:bg-bg-card"
          style={{ padding: '5px 10px', fontSize: 12, fontWeight: 500, color: 'var(--color-fg-secondary)', border: '1px solid var(--color-border)' }}
        >
          <CheckCircle className="w-3 h-3" />Done
        </button>
        <button
          type="button"
          onClick={handleDismiss}
          disabled={dismiss.isPending}
          className="flex items-center gap-1.5 rounded-md transition-colors disabled:opacity-50 hover:bg-bg-card"
          style={{ padding: '5px 10px', fontSize: 12, fontWeight: 500, color: 'var(--color-fg-muted)', border: '1px solid var(--color-border)' }}
        >
          <XCircle className="w-3 h-3" />Dismiss
        </button>
      </div>
    </div>
  )
}
